import { IPerformanceCapture } from '../../domain/interfaces/IPerformanceCapture';
import { LogEntry } from '../../domain/types';

export class PerformanceCapture implements IPerformanceCapture {
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;
  private observers: PerformanceObserver[] = [];

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    
    this.observeWebVitals();
    this.observeLongTasks();
    this.observeResources();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.callback = null;
    this.isActive = false;
  }

  private observeWebVitals(): void {
    // Observe Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          
          if (this.callback && lastEntry) {
            const value = Math.round(lastEntry.renderTime || lastEntry.loadTime);
            const rating = this.getVitalRating('LCP', value);
            
            this.callback({
              level: 'info',
              msg: `LCP: ${value}ms`,
              ts: new Date().toISOString(),
              source: 'browser',
              meta: {
                type: 'performance',
                metric: 'LCP',
                value: value.toString(),
                rating,
                url: window.location.href
              }
            });
          }
        });
        
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        this.observers.push(lcpObserver);
      } catch (e) {
        // LCP not supported
      }

      // Observe First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry: any) => {
            if (this.callback && entry.processingStart) {
              const value = Math.round(entry.processingStart - entry.startTime);
              const rating = this.getVitalRating('FID', value);
              
              this.callback({
                level: 'info',
                msg: `FID: ${value}ms`,
                ts: new Date().toISOString(),
                meta: {
                  type: 'performance',
                  metric: 'FID',
                  value: value.toString(),
                  rating,
                  url: window.location.href
                }
              });
            }
          });
        });
        
        fidObserver.observe({ type: 'first-input', buffered: true });
        this.observers.push(fidObserver);
      } catch (e) {
        // FID not supported
      }

      // Observe Cumulative Layout Shift (CLS)
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              
              if (this.callback) {
                const rating = this.getVitalRating('CLS', clsValue);
                
                this.callback({
                  level: 'info',
                  msg: `CLS: ${clsValue.toFixed(3)}`,
                  ts: new Date().toISOString(),
                  meta: {
                    type: 'performance',
                    metric: 'CLS',
                    value: clsValue.toString(),
                    rating,
                    url: window.location.href
                  }
                });
              }
            }
          });
        });
        
        clsObserver.observe({ type: 'layout-shift', buffered: true });
        this.observers.push(clsObserver);
      } catch (e) {
        // CLS not supported
      }
    }
  }

  private observeLongTasks(): void {
    if ('PerformanceObserver' in window && 'PerformanceLongTaskTiming' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry) => {
            if (this.callback && entry.duration > 50) {
              this.callback({
                level: 'warn',
                msg: `Long task: ${Math.round(entry.duration)}ms`,
                ts: new Date().toISOString(),
                meta: {
                  type: 'performance',
                  metric: 'longTask',
                  value: Math.round(entry.duration).toString(),
                  url: window.location.href
                }
              });
            }
          });
        });
        
        longTaskObserver.observe({ type: 'longtask' });
        this.observers.push(longTaskObserver);
      } catch (e) {
        // Long tasks not supported
      }
    }
  }

  private observeResources(): void {
    window.addEventListener('error', this.handleResourceError, true);
  }

  private handleResourceError = (event: ErrorEvent): boolean => {
    const target = event.target;
    
    if (target && target !== window && target instanceof HTMLElement) {
      const tagName = target.tagName?.toLowerCase();
      
      if (tagName === 'img' || tagName === 'script' || tagName === 'link') {
        const src = (target as any).src || (target as any).href;
        
        if (this.callback && src) {
          this.callback({
            level: 'error',
            msg: `Resource failed to load: ${src}`,
            ts: new Date().toISOString(),
            source: 'browser',
            meta: {
              type: 'resource',
              target: tagName,
              value: src,
              url: window.location.href
            }
          });
        }
      }
    }
    
    return true;
  };

  private getVitalRating(metric: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds: { [key: string]: { good: number; poor: number } } = {
      LCP: { good: 2500, poor: 4000 },
      FID: { good: 100, poor: 300 },
      CLS: { good: 0.1, poor: 0.25 },
      TTFB: { good: 800, poor: 1800 }
    };

    const threshold = thresholds[metric];
    if (!threshold) return 'good';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }
}